"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var React = require("react");
var classNames = require("classnames");
var ScreenReaderOnly_1 = require("@oui/core/ScreenReaderOnly");
var Messages = require("../../codegen/Messages");
var Aria_1 = require("../../resources/Aria");
var THeaderCell_1 = require("./THeaderCell");
var TRow_1 = require("./TRow");
var TCell_1 = require("./TCell");
var TNumberCell_1 = require("./TNumberCell");
var TDateCell_1 = require("./TDateCell");
var TActionMenuCell_1 = require("./TActionMenuCell");
var TCheckboxCell_1 = require("./TCheckboxCell");
var TErrorCell_1 = require("./TErrorCell");
var TLoadingCell_1 = require("./TLoadingCell");
var TLastRowHintCell_1 = require("./TLastRowHintCell");
var TBanner_1 = require("./TBanner");
var types_1 = require("./types");
var util_1 = require("../../utils/util");
// invisible table header for screen readers, used to add rowHeaders
// put the header as the last column to avoid breaking E2E tests which use column coordinates as a locator
var hiddenTableHeader = function (readerId, scope) { return (React.createElement("th", { className: "oui-table__column__hidden", scope: scope },
    React.createElement(ScreenReaderOnly_1.default, null, readerId))); };
/**
 * OUI table component.
 *
 * @oui-component Table
 */
var Table = /** @class */ (function (_super) {
    __extends(Table, _super);
    function Table(props) {
        var _this = _super.call(this, props) || this;
        // Used to initiate handshake between checkbox onClick and onChange (since onChange does not have shiftKey property,
        // need to store it onClick and then pass into onChange handler to enable (de)selecting a range of rows)
        _this.selectWithShiftKey = false;
        _this.getHeaderRowCells = _this.getHeaderRowCells.bind(_this);
        _this.getHeaderRow = _this.getHeaderRow.bind(_this);
        _this.getBodyRowCells = _this.getBodyRowCells.bind(_this);
        _this.onCheckboxClick = _this.onCheckboxClick.bind(_this);
        _this.getBodyRows = _this.getBodyRows.bind(_this);
        return _this;
    }
    Table.prototype.getHeaderRowCells = function () {
        var _this = this;
        var _a = this.props, tableId = _a.id, data = _a.data, columns = _a.columns, sortOrder = _a.sortOrder, updateSortOrder = _a.updateSortOrder, disableControls = _a.disableControls, onSelect = _a.onSelect, onSelectAll = _a.onSelectAll, loadingText = _a.loadingText;
        var hasSelectAll = !!(onSelect && onSelectAll);
        var currentSortColumnId = sortOrder && sortOrder.columnId;
        var currentSortDirection = sortOrder && sortOrder.direction;
        return columns.map(function (column, index) {
            var id = column.id, header = column.header, iconText = column.iconText, defaultSortDirection = column.defaultSortDirection, width = column.width;
            var sortHandler = defaultSortDirection &&
                updateSortOrder &&
                (function () {
                    updateSortOrder({
                        columnId: id,
                        direction: id === currentSortColumnId
                            ? THeaderCell_1.invert(currentSortDirection)
                            : defaultSortDirection
                    });
                });
            var dataCellType = _this.isDataCell(column.cell) && column.cell.type;
            return (React.createElement(THeaderCell_1.default, { key: "header-" + index, children: header, id: tableId && tableId + "-" + id + "-header", borderLeft: hasSelectAll || index !== 0, width: width, iconText: iconText, defaultSortDirection: defaultSortDirection, sortDirection: id === currentSortColumnId && currentSortDirection, sortHandler: sortHandler, disabled: disableControls || !!loadingText || !data.length, type: dataCellType }));
        });
    };
    Table.prototype.getHeaderRow = function () {
        var _a = this.props, id = _a.id, data = _a.data, disableControls = _a.disableControls, rowId = _a.rowId, selectedIds = _a.selectedIds, onSelect = _a.onSelect, onSelectAll = _a.onSelectAll, renderActions = _a.renderActions;
        var checkboxCell = React.createElement(TCell_1.default, { role: Aria_1.Roles.Presentation, "aria-hidden": "true" }); // Default to empty, hidden cell
        var actionMenuCell = null; // Default to null
        if (!onSelect) {
            // If no onSelect, table rows are not selectable (so don't render any multiselect)
            checkboxCell = null;
        }
        else if (onSelectAll) {
            var checked = selectedIds &&
                !!selectedIds.length &&
                data &&
                data.length &&
                data.every(function (row) { return selectedIds.includes(rowId(row)); });
            checkboxCell = (React.createElement(TCheckboxCell_1.default, { checked: checked, disabled: disableControls, id: id && id + "-header-checkbox", key: "header-checkbox", label: Messages.Table.selectAll(), ariaLabel: Messages.Table.selectAll(), onInputChange: onSelectAll }));
        }
        if (renderActions) {
            actionMenuCell = React.createElement(TCell_1.default, { role: Aria_1.Roles.Presentation }); // Empty cell
        }
        return (React.createElement(TRow_1.default, { id: id },
            checkboxCell,
            this.getHeaderRowCells(),
            actionMenuCell,
            hiddenTableHeader(Messages.Table.rowHeader(), "col")));
    };
    /**
     * Typed Data Cell helper functions
     */
    Table.prototype.isDataCell = function (cell) {
        return typeof cell === "object" && cell.hasOwnProperty("type");
    };
    Table.prototype.renderDataCell = function (column, row, key) {
        var dataCell = column.cell;
        switch (dataCell.type) {
            // numbers
            case types_1.DataCellTypes.Number:
                var numberValue = dataCell.data(row);
                return React.createElement(TNumberCell_1.default, { key: key, value: numberValue });
            // dates
            case types_1.DataCellTypes.LongDate:
            case types_1.DataCellTypes.RelativeDate:
                var dateValue = dataCell.data(row);
                return React.createElement(TDateCell_1.default, { key: key, value: dateValue, type: dataCell.type });
            // strings (default)
            case types_1.DataCellTypes.String:
            default:
                var stringValue = dataCell.data(row) || "-";
                return React.createElement(TCell_1.default, { key: key }, stringValue);
        }
    };
    Table.prototype.getBodyRowCells = function (row) {
        var _this = this;
        var _a = this.props, columns = _a.columns, rowId = _a.rowId;
        return columns.map(function (column, index) {
            var key = rowId(row) + "-" + index;
            if (_this.isDataCell(column.cell)) {
                return _this.renderDataCell(column, row, key);
            }
            else {
                return (React.createElement(TCell_1.default, { key: key }, column.cell(row)));
            }
        });
    };
    Table.prototype.getBodyRowCheckboxCell = function (row) {
        var _this = this;
        var _a = this.props, tableId = _a.id, onSelect = _a.onSelect, disableControls = _a.disableControls, readerRowId = _a.readerRowId, rowId = _a.rowId, isSelectDisabled = _a.isSelectDisabled, selectDisabledText = _a.selectDisabledText;
        if (!onSelect)
            return null;
        var id = rowId(row);
        var onInputChange = function () {
            onSelect(id, _this.selectWithShiftKey);
            _this.selectWithShiftKey = false; // Complete handshake with onCheckboxClick
        };
        var disabled = (isSelectDisabled && isSelectDisabled(row)) || disableControls;
        var readerId = readerRowId ? readerRowId(row) : rowId(row);
        return (React.createElement(TCheckboxCell_1.default, { checked: this.isRowSelected(row), disabled: disabled, disabledText: disabled && (selectDisabledText || Messages.Table.selectDisabled()), id: tableId && tableId + "-" + id + "-checkbox", key: id + "-checkbox", label: "" + Messages.Table.selectRow(readerId), ariaLabel: "" + Messages.Table.selectRow(readerId), onInputChange: onInputChange, onInputClick: this.onCheckboxClick }));
    };
    Table.prototype.isRowSelected = function (row) {
        var _a = this.props, selectedIds = _a.selectedIds, rowId = _a.rowId, isSelectDisabled = _a.isSelectDisabled;
        var id = rowId(row);
        var isSelected = !!selectedIds && !!selectedIds.length && selectedIds.includes(id);
        // if we're disabled at all, we need to return false
        // otherwise, return the second half
        if (isSelectDisabled) {
            return !isSelectDisabled(row) && isSelected;
        }
        return isSelected;
    };
    Table.prototype.hasActions = function () {
        return !!this.props.renderActions;
    };
    Table.prototype.onCheckboxClick = function (e) {
        // If shiftKey, initiate handshake with onSelect handler
        if (e.shiftKey) {
            this.selectWithShiftKey = true;
        }
    };
    Table.prototype.getBodyRows = function () {
        var _this = this;
        var _a = this.props, tableId = _a.id, data = _a.data, readerRowId = _a.readerRowId, rowId = _a.rowId, renderActions = _a.renderActions, renderRow = _a.renderRow, rowStatus = _a.rowStatus, portalClass = _a.portalClass;
        // Each row consists of a checkbox cell and then a cell for each defined column
        return !data
            ? []
            : data.map(function (row) {
                if (renderRow) {
                    return renderRow(row, _this);
                }
                var id = rowId(row);
                var readerId = readerRowId ? readerRowId(row) : id;
                var actionMenuCell = null;
                if (renderActions) {
                    actionMenuCell = (React.createElement(TActionMenuCell_1.default, { actions: renderActions(row), label: Messages.Table.menuCell(readerId), ariaLabel: Messages.Table.menuCell(readerId), portalClass: portalClass }));
                }
                return (React.createElement(TRow_1.default, { key: id + "-table-row", id: tableId && tableId + "-row-" + id, isActive: _this.isRowSelected(row), status: rowStatus(row) },
                    _this.getBodyRowCheckboxCell(row),
                    _this.getBodyRowCells(row),
                    actionMenuCell,
                    hiddenTableHeader(readerId, "row")));
            });
    };
    Table.prototype.render = function () {
        var _a = this.props, id = _a.id, data = _a.data, columns = _a.columns, label = _a.label, labelledBy = _a.labelledBy, describedBy = _a.describedBy, disableControls = _a.disableControls, horizontalScroll = _a.horizontalScroll, errorText = _a.errorText, errorIconText = _a.errorIconText, loadingText = _a.loadingText, noItemsText = _a.noItemsText, header = _a.header, footer = _a.footer, renderActions = _a.renderActions, renderRow = _a.renderRow, lastRowHint = _a.lastRowHint, testId = _a.testId, noItemsContent = _a.noItemsContent, caption = _a.caption, onSelect = _a.onSelect, onSelectAll = _a.onSelectAll;
        var tableClassName = classNames("oui-table oui-table-divider oui-table-middle oui-margin-remove oui-text-nowrap", {
            "oui-table-hover": !disableControls &&
                !loadingText &&
                !errorText &&
                data &&
                !!data.length
        });
        // Calculate total column span for error and loading cells
        // Add +1 for checkboxes, and +1 conditionally for action menu
        // because both of these are not included in columns
        var totalColumns = columns.length + (renderActions ? 2 : 1);
        var headerRow = this.getHeaderRow();
        var bodyRows = [];
        if (errorText) {
            // Error always takes precedence
            bodyRows = [
                React.createElement(TRow_1.default, { key: "error-row", id: id && id + "-error-row" },
                    React.createElement(TErrorCell_1.default, { colSpan: totalColumns, text: errorText, iconText: errorIconText }),
                    hiddenTableHeader(Messages.Table.tableError(), "row"))
            ];
        }
        else if (loadingText) {
            // Then loading
            bodyRows = [
                React.createElement(TRow_1.default, { key: "loading-row", id: id && id + "-loading-row" },
                    React.createElement(TLoadingCell_1.default, { colSpan: totalColumns, text: loadingText }),
                    hiddenTableHeader(Messages.Table.tableStatus(), "row"))
            ];
        }
        else if (!data.length) {
            bodyRows = [
                React.createElement(TRow_1.default, { key: "no-items-row", id: id && id + "-no-items-row" },
                    React.createElement(TCell_1.default, { colSpan: totalColumns },
                        React.createElement("div", { className: "oui-text-center", role: Aria_1.Roles.Status }, noItemsContent || noItemsText || Messages.Table.noItems())),
                    hiddenTableHeader(Messages.Table.tableStatus(), "row"))
            ];
        }
        else {
            bodyRows = this.getBodyRows();
        }
        // add last row hint when available
        lastRowHint &&
            bodyRows.push(React.createElement(TRow_1.default, { key: "last-row-hint", id: id && id + "-last-row-hint", isActive: false },
                React.createElement(TLastRowHintCell_1.default, { colSpan: renderRow ? totalColumns + 1 : totalColumns }, lastRowHint),
                hiddenTableHeader("Hint", "row")));
        // TODO: overflow classes have been removed for non-scroll cases to allow action menu to be used.
        // This causes a bug where the menu is trimmed when the table has horizontal scroll enabled
        // and will also block the development of filters on the right. https://jira.oci.oraclecorp.com/browse/JANUS-1292
        var tableWrapperClassName = classNames("oui-width-expand", "oui-height-1-1", { "oui-overflow-auto": horizontalScroll });
        var wrapperParentClassName = classNames("oui-grid-collapse", "oui-margin-remove", { "oui-overflow-x-hidden": horizontalScroll });
        var busy = loadingText && !errorText ? true : undefined;
        var multiselecTable = onSelect ? (onSelectAll ? true : false) : undefined;
        return (React.createElement("section", { className: "oui-section oui-section-default oui-border-all oui-border-radius-all oui-padding-remove" },
            header && React.createElement(TBanner_1.TBannerHeader, null, header),
            React.createElement("div", { className: wrapperParentClassName, "data-oui-grid": "" },
                React.createElement("div", { className: tableWrapperClassName },
                    React.createElement("table", __assign({ className: tableClassName, "data-test-id": testId }, util_1.ariaProps({
                        busy: busy,
                        describedBy: describedBy,
                        label: label,
                        labelledBy: labelledBy,
                        multiselecTable: multiselecTable
                    })),
                        React.createElement("caption", null,
                            React.createElement(ScreenReaderOnly_1.default, null, caption)),
                        React.createElement("thead", null, headerRow),
                        React.createElement("tbody", { className: classNames({ "oui-text-wrap": !horizontalScroll }) }, bodyRows)))),
            footer && React.createElement(TBanner_1.TBannerFooter, null, footer)));
    };
    Table.defaultProps = {
        rowStatus: function () { return TRow_1.RowStatus.Default; }
    };
    return Table;
}(React.Component));
exports.default = Table;
//# sourceMappingURL=Table.js.map